import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight } from "lucide-react";
import { COMPANY } from "../lib/site-data";

const Section = ({ title, children, id }) => (
  <section id={id} className="scroll-mt-32">
    <h2 className="font-display text-2xl lg:text-3xl font-bold text-[color:var(--brand-navy)] mb-4">
      {title}
    </h2>
    <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
  </section>
);

export default function Accessibility() {
  return (
    <div data-testid="accessibility-page">
      <section className="bg-[color:var(--brand-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-20" />
        <div className="container-tg relative py-24 lg:py-32">
          <div className="overline text-[color:var(--brand-yellow)] mb-6">
            Accessibility
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none max-w-4xl">
            Website Accessibility Statement
          </h1>
          <p className="mt-6 text-white/70 text-sm uppercase tracking-widest">
            Last updated: February 27, 2026
          </p>
        </div>
      </section>

      <section className="container-tg py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start">
          <div className="border-l-2 border-[color:var(--brand-yellow)] pl-4">
            <div className="overline mb-2">On this page</div>
            <ul className="space-y-2 text-sm">
              {[
                ["commitment", "Our Commitment"],
                ["standards", "Standards"],
                ["tools", "Tools"],
                ["ongoing", "Ongoing Improvement"],
                ["third-party", "Third-Party Content"],
                ["feedback", "Feedback & Assistance"],
                ["good-faith", "Good-Faith Efforts"],
                ["terms", "Terms Clause"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a
                    href={`#${id}`}
                    className="text-slate-600 hover:text-[color:var(--brand-navy)] font-medium"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-12">
          <Section id="commitment" title="Our Commitment to Accessibility">
            <p>
              We are committed to ensuring digital accessibility for people
              with disabilities. We are continually improving the user
              experience for everyone and applying relevant accessibility
              standards to enhance usability and inclusivity. Our goal is to
              provide a website that is accessible to the widest possible
              audience, regardless of technology or ability.
            </p>
          </Section>

          <Section id="standards" title="Accessibility Standards">
            <p>
              We strive to conform to the Web Content Accessibility Guidelines
              (WCAG) 2.1 Level AA, which are widely recognized as the industry
              standard for website accessibility. Accessibility is an ongoing
              process, and we are actively working toward improving the
              accessibility and usability of our website.
            </p>
          </Section>

          <Section id="tools" title="Accessibility Technology & Tools">
            <p>
              To support our accessibility efforts, we have deployed
              industry-standard accessibility technology designed to enhance
              usability and assist visitors with disabilities. These tools may
              provide features such as:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Screen reader enhancements</li>
              <li>Keyboard navigation improvements</li>
              <li>Color contrast adjustments</li>
              <li>Text size and spacing controls</li>
              <li>Focus indicators</li>
              <li>Additional accessibility interface options</li>
            </ul>
            <p>
              This technology helps us improve accessibility while we continue
              broader auditing, remediation, and development efforts.
            </p>
          </Section>

          <Section id="ongoing" title="Ongoing Improvement">
            <p>Accessibility is an ongoing initiative. We are:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Reviewing site content and functionality regularly</li>
              <li>Working with developers and accessibility tools</li>
              <li>Identifying and remediating barriers</li>
              <li>Incorporating accessibility into future design and development</li>
            </ul>
            <p>
              We understand accessibility is a continuous journey and are
              committed to making improvements over time.
            </p>
          </Section>

          <Section id="third-party" title="Third-Party Content">
            <p>
              Some areas of the website may include third-party content,
              plugins, or integrations that are not fully controlled by us.
              While we encourage vendors to provide accessible content, we
              cannot guarantee accessibility of third-party features. However,
              we welcome feedback regarding any issues encountered.
            </p>
          </Section>

          <Section id="feedback" title="Feedback & Assistance">
            <p>
              If you experience difficulty accessing any part of this website,
              need assistance, or would like to request content in an
              alternative format, please contact us. Please include:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>The page URL</li>
              <li>Description of the issue</li>
              <li>Your preferred contact method</li>
            </ul>
            <div className="mt-6 bg-white border border-[color:var(--surface-border)] p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-3 text-slate-700 hover:text-[color:var(--brand-navy)]"
              >
                <span className="w-10 h-10 bg-[color:var(--brand-yellow)] text-[color:var(--brand-navy)] flex items-center justify-center">
                  <Mail className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Email</div>
                  <div className="font-semibold">{COMPANY.email}</div>
                </div>
              </a>
              <a
                href={`tel:${COMPANY.phone}`}
                className="flex items-center gap-3 text-slate-700 hover:text-[color:var(--brand-navy)]"
              >
                <span className="w-10 h-10 bg-[color:var(--brand-navy)] text-[color:var(--brand-yellow)] flex items-center justify-center">
                  <Phone className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Phone</div>
                  <div className="font-semibold">{COMPANY.phone}</div>
                </div>
              </a>
            </div>
            <p className="text-sm text-slate-500">
              We aim to respond within a reasonable timeframe and will make
              reasonable efforts to provide accessible alternatives.
            </p>
            <Link to="/contact" className="btn-primary mt-4">
              Visit Contact Page <ArrowRight className="w-4 h-4" />
            </Link>
          </Section>

          <Section id="good-faith" title="Good-Faith Efforts">
            <p>
              We are making good-faith efforts to comply with accessibility
              standards and improve usability for individuals with
              disabilities. Despite our efforts, some content may not yet
              fully meet the strictest accessibility standards due to the
              evolving nature of technology and ongoing updates. We
              appreciate your understanding as we continue improving
              accessibility.
            </p>
          </Section>

          <Section id="terms" title="Terms & Conditions — Accessibility Clause">
            <p>
              We are committed to providing an accessible website experience
              for all users and strive to follow recognized accessibility
              standards, including WCAG guidelines. We have implemented
              industry-standard accessibility tools and continue to review,
              update, and improve our website to enhance accessibility.
              Because accessibility is an ongoing process, some areas of the
              website may not yet fully conform to all accessibility
              standards. If you encounter accessibility barriers, please
              contact us so we can provide assistance, offer alternative
              access, and address the issue where feasible. Nothing in these
              Terms is intended to limit any rights available under
              applicable accessibility laws.
            </p>
          </Section>
        </div>
      </section>
    </div>
  );
}
